import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getNuCallWidget, NuCallWidgetConfig } from '../../services/api/nucallService';
import { loadNuCall } from './nucallLoader';

/**
 * Mounts the NuCall voice assistant for signed-in users.
 *
 * It renders nothing itself — the SDK owns its own DOM. It must live in
 * App.tsx, NOT in Layout.tsx: Layout is rendered per page, so a route change
 * unmounts and remounts it, which would tear down a call in progress.
 *
 * Every failure path is silent. A third-party assistant being unavailable is
 * not something to interrupt someone's work over.
 */

// Never show the assistant to anonymous visitors: without a signed identity
// the session is anonymous, and the widget key would be exposed on pages that
// do not need it.
const PUBLIC_PREFIXES = ['/public/', '/login', '/signup', '/register', '/reset-password', '/payments/callback', '/legal'];

export default function NuCallWidget() {
  const { isAuthenticated, user } = useAuth();
  const { language, isRTL } = useLanguage();
  const location = useLocation();

  const [config, setConfig] = useState<NuCallWidgetConfig | null>(null);
  const bootedRef = useRef(false);
  const refreshTimerRef = useRef<number | null>(null);

  const locale: 'ar' | 'en' = language === 'ar' ? 'ar' : 'en';
  const onPublicPage = PUBLIC_PREFIXES.some((p) => location.pathname.startsWith(p));
  // Not for contractors: their portal is all they get (the server refuses the rest)
  const eligible = Boolean(isAuthenticated && user && user.role !== 'contractor' && !onPublicPage);

  // Fetch — keyed on identity, deliberately not on the route. Re-fetching per
  // navigation would churn tokens and could interrupt a live call.
  useEffect(() => {
    if (!eligible) {
      setConfig(null);
      return;
    }
    const ac = new AbortController();
    getNuCallWidget(locale, ac.signal)
      .then((cfg) => setConfig(cfg.enabled ? cfg : null))
      .catch(() => setConfig(null)); // 402/403/5xx/offline → simply off
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligible, user?.id]);

  // Load + boot, once per configuration.
  useEffect(() => {
    if (!config?.enabled || !config.widget_key) return;
    let cancelled = false;

    loadNuCall({
      src: config.script_url!,
      integrity: config.script_integrity || undefined,
      widgetKey: config.widget_key,
      identityToken: config.identity?.token,
      position: config.launcher_position,
      lang: config.locale,
    })
      .then(() => {
        if (cancelled) return;
        bootedRef.current = true;
      })
      .catch(() => {
        /* unavailable — stay silent */
      });

    return () => {
      cancelled = true;
    };
  }, [config?.widget_key, config?.enabled, config?.script_url, config?.script_integrity, config?.launcher_position, config?.locale, config?.identity?.token]);

  // Tear down on logout. The script tag stays; shutdown() is the documented
  // teardown and boot() brings it back without a page reload.
  useEffect(() => {
    if (eligible || !bootedRef.current) return;
    window.NuCall?.('shutdown');
    bootedRef.current = false;
  }, [eligible]);

  // Language flips update in place. Re-booting here would drop a live call.
  useEffect(() => {
    if (!bootedRef.current) return;
    window.NuCall?.('update', { lang: locale, rtl: isRTL });
  }, [locale, isRTL]);

  // Per-route visibility, without re-booting.
  useEffect(() => {
    if (!bootedRef.current || !config) return;
    const hidden = (config.hidden_path_prefixes ?? []).some((p) => location.pathname.startsWith(p));
    window.NuCall?.(hidden ? 'hide' : 'show');
  }, [location.pathname, config]);

  // Refresh the identity token before it expires, and after the tab has been
  // in the background long enough for it to have lapsed.
  useEffect(() => {
    if (!config?.identity || !eligible) return;

    const schedule = () => {
      if (refreshTimerRef.current) window.clearTimeout(refreshTimerRef.current);
      const msUntil = Math.max(5_000, (config.identity!.refresh_after - Date.now() / 1000) * 1000);
      refreshTimerRef.current = window.setTimeout(refresh, msUntil);
    };

    const refresh = () => {
      getNuCallWidget(locale)
        .then((cfg) => {
          if (!cfg.enabled || !cfg.identity) return;
          window.NuCall?.('identify', { user_token: cfg.identity.token });
          setConfig(cfg);
        })
        .catch(() => {
          /* the widget keeps working; the caller is treated as anonymous */
        });
    };

    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() / 1000 > config.identity!.refresh_after) refresh();
    };

    schedule();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      if (refreshTimerRef.current) window.clearTimeout(refreshTimerRef.current);
      document.removeEventListener('visibilitychange', onVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config?.identity?.expires_at, eligible, locale]);

  return null;
}
